import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Banknote, Calendar, X, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { FieldError } from "../components/FieldError";
import { formatApiError } from "../lib/utils";

function validateJob(f) {
  const errs = {};
  if (!f.title.trim()) errs.title = "Please enter a job title";
  else if (f.title.trim().length < 5) errs.title = "Job title should be at least 5 characters";
  if (!f.description.trim()) errs.description = "Please enter a job description";
  else if (f.description.trim().length < 20) errs.description = "Please describe the job in at least 20 characters";
  const min = f.budget_min === "" ? null : Number(f.budget_min);
  const max = f.budget_max === "" ? null : Number(f.budget_max);
  if ((min !== null && (isNaN(min) || min < 0)) || (max !== null && (isNaN(max) || max < 0))) {
    errs.budget = "Budget must be a positive number";
  } else if (min !== null && max !== null && min > max) {
    errs.budget = "Minimum budget can't be more than the maximum";
  }
  if (f.deadline && new Date(f.deadline) < new Date(new Date().toDateString())) {
    errs.deadline = "Deadline can't be in the past";
  }
  return errs;
}

export default function PostJob() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    skills_required: [],
    location: "",
    budget_min: "",
    budget_max: "",
    duration: "",
    deadline: "",
    is_remote: false,
    company: "",
    budget_type: "project",
  });

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please log in to post a job");
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`),
      ]);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, budget: undefined }));
  };

  const addSkill = (skill) => {
    if (skill && !formData.skills_required.includes(skill)) {
      setFormData((prev) => ({
        ...prev,
        skills_required: [...prev.skills_required, skill],
      }));
    }
    setSkillInput("");
  };

  const removeSkill = (skill) => {
    setFormData((prev) => ({
      ...prev,
      skills_required: prev.skills_required.filter((s) => s !== skill),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validateJob(formData);
    setErrors(found);
    if (Object.keys(found).length) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        deadline: formData.deadline || null,
        company: formData.company.trim() || null,
      };

      await axios.post(`${API}/jobs`, submitData, { withCredentials: true });
      toast.success("Job posted successfully!");
      navigate("/jobs");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to post job"));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="post-job-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Briefcase className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h1 className="font-heading text-title uppercase">
            POST A <span className="text-orange-500">JOB</span>
          </h1>
          <p className="text-zinc-500 mt-2">
            Find the perfect creative talent for your project
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-6">
            {/* Job Title */}
            <div>
              <Label htmlFor="title" className="text-zinc-300">
                Job Title *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => updateForm("title", e.target.value)}
                placeholder="e.g., Videographer needed for corporate event"
                className="mt-2 bg-zinc-800 border-zinc-700"
                data-testid="job-title-input"
                aria-invalid={!!errors.title}
              />
              <FieldError message={errors.title} testId="job-title-error" />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-zinc-300">
                Job Description *
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => updateForm("description", e.target.value)}
                placeholder="Describe the project, requirements, and what you're looking for..."
                className="mt-2 min-h-40 bg-zinc-800 border-zinc-700"
                data-testid="job-description-input"
                aria-invalid={!!errors.description}
              />
              <FieldError message={errors.description} testId="job-description-error" />
            </div>

            {/* Skills Required */}
            <div>
              <Label className="text-zinc-300">Skills Required</Label>
              <div className="flex gap-2 mt-2">
                <Select onValueChange={(value) => addSkill(value)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                    <SelectValue placeholder="Select a skill" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {categories.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    placeholder="Or type custom skill"
                    className="bg-zinc-800 border-zinc-700"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSkill(skillInput);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addSkill(skillInput)}
                    className="border-zinc-700"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {formData.skills_required.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.skills_required.map((skill) => (
                    <Badge
                      key={skill}
                      className="bg-orange-900/30 text-orange-400 border border-orange-800 cursor-pointer"
                      onClick={() => removeSkill(skill)}
                    >
                      {skill}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <Label className="text-zinc-300">Location</Label>
              <Select
                value={formData.location}
                onValueChange={(value) => updateForm("location", value)}
              >
                <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-300">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {locations.map((loc) => (
                    <SelectItem key={loc.location_id} value={loc.name}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <Checkbox
                  checked={formData.is_remote}
                  onCheckedChange={(checked) => updateForm("is_remote", checked)}
                  className="border-zinc-600 data-[state=checked]:bg-orange-600"
                />
                <span className="text-sm text-zinc-400">Remote work possible</span>
              </label>
            </div>

            {/* Budget */}
            <div>
              <Label className="text-zinc-300">Budget Range (ZAR)</Label>
              <div className="flex items-center gap-3 mt-2">
                <Input
                  type="number"
                  value={formData.budget_min}
                  onChange={(e) => updateForm("budget_min", e.target.value)}
                  placeholder="Min"
                  min="0"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="job-budget-min-input"
                />
                <span className="text-zinc-600">-</span>
                <Input
                  type="number"
                  value={formData.budget_max}
                  onChange={(e) => updateForm("budget_max", e.target.value)}
                  placeholder="Max"
                  min="0"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="job-budget-max-input"
                />
              </div>
              <FieldError message={errors.budget} testId="job-budget-error" />
              <div className="flex gap-4 mt-3 text-sm text-zinc-400" role="radiogroup" aria-label="Budget type">
                {[["project", "Whole project"], ["day", "Per day"]].map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="budget_type"
                      value={value}
                      checked={formData.budget_type === value}
                      onChange={() => updateForm("budget_type", value)}
                      className="accent-orange-600"
                      data-testid={`job-budget-type-${value}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Company (optional) */}
            <div>
              <Label htmlFor="company" className="text-zinc-300">
                Company / organisation <span className="text-zinc-500 font-normal">(optional)</span>
              </Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => updateForm("company", e.target.value)}
                placeholder="Leave blank to show as Private client"
                maxLength={120}
                className="mt-2 bg-zinc-800 border-zinc-700"
                data-testid="job-company-input"
              />
            </div>

            {/* Duration & Deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration" className="text-zinc-300">
                  Duration
                </Label>
                <Select
                  value={formData.duration}
                  onValueChange={(value) => updateForm("duration", value)}
                >
                  <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-300">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="1 day">1 Day</SelectItem>
                    <SelectItem value="2-3 days">2-3 Days</SelectItem>
                    <SelectItem value="1 week">1 Week</SelectItem>
                    <SelectItem value="2-4 weeks">2-4 Weeks</SelectItem>
                    <SelectItem value="1-3 months">1-3 Months</SelectItem>
                    <SelectItem value="3+ months">3+ Months</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="deadline" className="text-zinc-300">
                  Application Deadline
                </Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => updateForm("deadline", e.target.value)}
                  className="mt-2 bg-zinc-800 border-zinc-700"
                  data-testid="job-deadline-input"
                />
                <FieldError message={errors.deadline} testId="job-deadline-error" />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/jobs")}
              className="flex-1 border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-orange-600 hover:bg-orange-500"
              data-testid="submit-job-btn"
            >
              {loading ? "Posting..." : "Post Job"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
